import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { FIELD } from "@/constants/testIds";

const blobToDataUrl = (blob) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(blob);
  });

export function VoiceNoteButton({ onResult }) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const handleStop = async () => {
    clearTimeout(timerRef.current);
    setState("processing");
    streamRef.current?.getTracks().forEach((t) => t.stop());
    const mime = recRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    try {
      const dataUrl = await blobToDataUrl(blob);
      const { data } = await api.post("/ai/transcribe-note", { data_url: dataUrl }, { timeout: 90000 });
      if (data.notes) {
        onResult(data.notes);
        toast.success("Voice note added to notes");
      } else {
        toast.info("Didn't catch that — try recording again");
      }
    } catch (e) {
      toast.error(apiErr(e, "Couldn't transcribe the note. Try again."));
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (navigator.onLine === false) { toast.error("Voice notes need a connection"); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice recording isn't supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined;
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      recRef.current = rec;
      rec.start();
      setState("recording");
      // Safety cap: auto-stop after 2 minutes so a forgotten recording can't balloon.
      timerRef.current = setTimeout(() => {
        if (recRef.current && recRef.current.state === "recording") {
          toast.info("Max 2 min reached — transcribing");
          recRef.current.stop();
        }
      }, 120000);
    } catch {
      toast.error("Microphone unavailable — check app permissions");
    }
  };

  const stop = () => { if (recRef.current && state === "recording") recRef.current.stop(); };

  if (state === "processing") {
    return (
      <Button type="button" size="sm" variant="outline" disabled data-testid={FIELD.voiceBtn}>
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Transcribing…
      </Button>
    );
  }
  if (state === "recording") {
    return (
      <Button type="button" size="sm" className="animate-pulse bg-rose-600 hover:bg-rose-700" onClick={stop} data-testid={FIELD.voiceStop}>
        <Square className="mr-1.5 h-4 w-4" /> Stop &amp; transcribe
      </Button>
    );
  }
  return (
    <Button type="button" size="sm" variant="outline" onClick={start} data-testid={FIELD.voiceBtn}>
      <Mic className="mr-1.5 h-4 w-4" /> Voice note
    </Button>
  );
}
